import { dataAttribute, DataAttributes } from "@bps/fluent-ui";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { FilterBar } from "@ui-components/filter-bar/FilterBar.tsx";
import { FilterBarProps } from "@ui-components/filter-bar/FilterBar.types.ts";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";

type CommunicationHistoryFilterProps = Pick<
  FilterBarProps<CommunicationHistoryFilterState>,
  "children"
>;

export interface CommunicationHistoryFilterState {
  search?: string;
  status?: string[];
  reason?: string[];
  method?: string[];
  startTime?: Date;
  endTime?: Date;
}

export const CommunicationHistoryFilterBase: React.FC<
  CommunicationHistoryFilterProps
> = ({ children }) => {
  const { comms } = useStores();

  const items: FilterBarProps<CommunicationHistoryFilterState>["items"] = [
    {
      type: "searchBox",
      name: "search",
      stickItem: true,
      props: {
        removeSpecialCharacters: false,
        autoFocus: true,
        placeholder: `Search for ${usePatientLabel(false)}`,
        styles: {
          clearButton: { display: "none" },
          root: { width: 400 }
        }
      }
    },
    {
      type: "optionsSelect",
      name: "status",
      props: {
        id: "comm-history-filter-status",
        placeholder: "Status",
        multiSelect: true,
        hideSearchOption: true,
        hideSelectAllButton: true,
        calloutWidth: 200,
        options: [
          { key: "pending", text: "Pending" },
          { key: "sent", text: "Sent" },
          { key: "delivered", text: "Delivered" }
        ]
      }
    },
    {
      type: "optionsSelect",
      name: "reason",
      props: {
        id: "comm-history-filter-reason",
        placeholder: "Reason",
        multiSelect: true,
        hideSearchOption: true,
        hideSelectAllButton: true,
        calloutWidth: 250,
        options: comms.ref.outboundCommTypes.keyTextValues
      }
    },

    {
      type: "optionsSelect",
      name: "method",
      props: {
        id: "comm-history-filter-method",
        placeholder: "Method",
        multiSelect: true,
        hideSelectAllButton: true,
        hideSearchOption: true,
        calloutWidth: 200,
        options: comms.ref.outboundCommChannels.keyTextValues
      }
    },
    {
      type: "datesRangePicker",
      name: "datesRangePicker",
      props: {
        id: "comm-history-filter-dates",
        startDateProps: { id: "comm-history-filter-start-date" },
        endDateProps: { id: "comm-history-filter-end-date" },
        startDateName: "startTime",
        endDateName: "endTime",
        placeholder: "Date(s)"
      }
    }
  ];
  return (
    <FilterBar<CommunicationHistoryFilterState>
      items={items}
      initialValues={undefined}
      {...dataAttribute(DataAttributes.Element, "communicationHistory-filter")}
    >
      {children}
    </FilterBar>
  );
};
export const CommunicationHistoryFilter = withFetch(
  x =>
    Promise.all([
      x.comms.ref.outboundCommTypes.load(),
      x.comms.ref.outboundCommChannels.load()
    ]),
  CommunicationHistoryFilterBase
);
